import * as dataCollectionTypes from '../../consts/uam/dataCollectionTypes';
import {setDiagnosticFlowDataCollectionInProgress} from '../../state/ducks/advancedDiagnostic';
import {
    setYapCancelDataCollectionInProgress,
    setYapSynchronizeDataCollectionInProgress,
    setYapSynchronizeDeviceDataCollectionReceived,
    setYapSynchronizeHolderDataCollectionReceived,
    setYapUsageFlowDataCollectionInProgress,
    setYapUsageFlowDeviceDataCollectionReceived,
    setYapUsageFlowHolderDataCollectionReceived,
} from '../../state/ducks/yapEncrypted';
import {
    selectDiagnosticFlowDataResult,
    selectIsDiagnosticFlowDataCollectionInProgress,
} from '../../state/selectors/advancedDiagnostic';
import {
    makeSelectIsYapSynchronizeDataCollectionInProgress,
    makeSelectIsYapSynchronizeDataCollectionReceived,
    makeSelectIsYapUsageFlowDataCollectionInProgress,
    makeSelectIsYapUsageFlowDataCollectionReceived,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import deviceTypesService from '../device/deviceTypesService';
import iotDeviceSettingsService from '../iotDevice/iotDeviceSettingsService';
import productService from '../product/productService';
import advanceDiagnosticService from '../yap/advanceDiagnosticService';

export const isHolderSyncFirst = (deviceType) => {
    return deviceTypesService.isP1V4(deviceType) && !deviceTypesService.isMono(deviceType);
};

export const setDataCollectionFlowInProgress = (
    isInProgress,
    dataCollectionType = dataCollectionTypes.DATA_COLLECTION_ALL,
    isHolder
) => {
    if (!isInProgress) {
        dispatch(setYapCancelDataCollectionInProgress(false));
    }

    switch (dataCollectionType) {
        case dataCollectionTypes.DATA_COLLECTION_GLOBAL:
            dispatch(setYapSynchronizeDataCollectionInProgress(isInProgress, isHolder));
            break;
        case dataCollectionTypes.DATA_COLLECTION_USAGE:
            dispatch(setYapUsageFlowDataCollectionInProgress(isInProgress));
            break;
        case dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC:
            dispatch(setDiagnosticFlowDataCollectionInProgress(isInProgress));
            break;
        case dataCollectionTypes.DATA_COLLECTION_ALL:
            dispatch(setYapSynchronizeDataCollectionInProgress(isInProgress));
            dispatch(setYapUsageFlowDataCollectionInProgress(isInProgress));
            dispatch(setDiagnosticFlowDataCollectionInProgress(isInProgress));
            break;
        default:
            break;
    }
};

export const setDataCollectionFlowReceived = (props) => {
    const {
        isReceived = false,
        isHolder = false,
        dataCollectionType = dataCollectionTypes.DATA_COLLECTION_ALL,
    } = props || {};

    if (isHolder) {
        switch (dataCollectionType) {
            case dataCollectionTypes.DATA_COLLECTION_GLOBAL:
                dispatch(setYapUsageFlowHolderDataCollectionReceived(isReceived));
                dispatch(setYapSynchronizeHolderDataCollectionReceived(isReceived));
                break;
            case dataCollectionTypes.DATA_COLLECTION_USAGE:
                dispatch(setYapUsageFlowHolderDataCollectionReceived(isReceived));
                break;
            case dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC:
                advanceDiagnosticService.setDiagnosticReceived({isHolder, isReceived});
                break;
            case dataCollectionTypes.DATA_COLLECTION_ALL:
                dispatch(setYapUsageFlowHolderDataCollectionReceived(isReceived));
                dispatch(setYapSynchronizeHolderDataCollectionReceived(isReceived));
                break;
            default:
                break;
        }
    } else {
        switch (dataCollectionType) {
            case dataCollectionTypes.DATA_COLLECTION_GLOBAL:
                dispatch(setYapUsageFlowDeviceDataCollectionReceived(isReceived));
                dispatch(setYapSynchronizeDeviceDataCollectionReceived(isReceived));
                break;
            case dataCollectionTypes.DATA_COLLECTION_USAGE:
                dispatch(setYapUsageFlowDeviceDataCollectionReceived(isReceived));
                break;
            case dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC:
                advanceDiagnosticService.setDiagnosticReceived({isHolder, isReceived});
                break;
            case dataCollectionTypes.DATA_COLLECTION_ALL:
                dispatch(setYapSynchronizeDeviceDataCollectionReceived(isReceived));
                dispatch(setYapUsageFlowDeviceDataCollectionReceived(isReceived));
                break;
            default:
                break;
        }
    }
};

export const getIsDataCollectionReceived = (dataCollectionType, isHolder) => {
    const state = getState();

    switch (dataCollectionType) {
        case dataCollectionTypes.DATA_COLLECTION_GLOBAL:
            return makeSelectIsYapSynchronizeDataCollectionReceived(isHolder)(state);
        case dataCollectionTypes.DATA_COLLECTION_USAGE:
            return makeSelectIsYapUsageFlowDataCollectionReceived(isHolder)(state);
        case dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC:
            const diagnosticResult = selectDiagnosticFlowDataResult(state);

            return isHolder ? diagnosticResult?.isHolderReceived : diagnosticResult?.isDeviceReceived;
        default:
            break;
    }

    return false;
};

export const getEntitiesToCollect = (dataCollectionType) => {
    switch (dataCollectionType) {
        case dataCollectionTypes.DATA_COLLECTION_GLOBAL:
            return ['SETTINGS', 'USAGE_NEWEST_FIRST', 'ERRORS', 'EVENTS'];
        case dataCollectionTypes.DATA_COLLECTION_USAGE:
            return ['USAGE_NEWEST_FIRST'];
        case dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC:
            return ['ERRORS'];
        default:
            break;
    }
};

export const getCurrentDataCollectionType = () => {
    const state = getState();

    const isUsageFlowSyncInProgress = makeSelectIsYapUsageFlowDataCollectionInProgress()(state);

    if (isUsageFlowSyncInProgress) return dataCollectionTypes.DATA_COLLECTION_USAGE;

    const isDiagnosticFlowSyncInProgress = selectIsDiagnosticFlowDataCollectionInProgress(state);

    if (isDiagnosticFlowSyncInProgress) return dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC;

    const isYapSynchronizeDataCollectionInProgress = makeSelectIsYapSynchronizeDataCollectionInProgress()(state);

    if (isYapSynchronizeDataCollectionInProgress) return dataCollectionTypes.DATA_COLLECTION_GLOBAL;

    return null;
};

export const isDataCollectionInProgress = (dataCollectionType) => {
    const state = getState();
    const isYapSynchronizeDataCollectionInProgress =
        makeSelectIsYapSynchronizeDataCollectionInProgress()(state) &&
        dataCollectionType === dataCollectionTypes.DATA_COLLECTION_GLOBAL;
    const isYapUsageFlowDataCollectionInProgress =
        makeSelectIsYapUsageFlowDataCollectionInProgress()(state) &&
        dataCollectionType === dataCollectionTypes.DATA_COLLECTION_USAGE;
    const isDiagnosticFlowDataCollectionInProgress =
        selectIsDiagnosticFlowDataCollectionInProgress(state) &&
        dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC;

    if (
        isYapUsageFlowDataCollectionInProgress ||
        isYapSynchronizeDataCollectionInProgress ||
        isDiagnosticFlowDataCollectionInProgress
    )
        return true;

    return false;
};

export const isDataCollectionShouldBeStopped = ({
    dataCollectionTypeData,
    isDiagnosticFlowDataCollectionInProgress,
    isYapUsageFlowDataCollectionInProgress,
    isYapSynchronizeDataCollectionInProgress,
    isGlobal,
    isUsage,
    isDiagnostic,
    isHolder,
    isCanBeStarted,
    isDeviceSyncInProgress,
    isHolderSyncInProgress,
    iotProduct,
    isDeviceActivated,
    isYapFirmwareInProgress,
    isFirmwareInProgress,
    isFindDeviceInProgress,
}) => {
    const {device, holder} = iotProduct || {};
    const {status: deviceStatus, is_device_in_sbl_mode, isP4, isHolderConnected} = device || {};
    const {status: holderStatus} = holder || {};
    const isHolderInSBLMode = iotDeviceSettingsService.isHolderInSBLMode(iotProduct);
    const isDeviceRegistered = productService.getIsRegistered(deviceStatus);
    const isHolderRegistered = productService.getIsRegistered(holderStatus);
    const isActivated = isP4 ? isDeviceActivated : true;
    const isDeviceDataCollectionReceived = dataCollectionTypeData.isDeviceDataCollectionReceived;
    const isHolderDataCollectionReceived = dataCollectionTypeData.isHolderDataCollectionReceived;

    const isNeedCancel =
        (isDiagnosticFlowDataCollectionInProgress && isDiagnostic) ||
        (isYapUsageFlowDataCollectionInProgress && isUsage) ||
        (isYapSynchronizeDataCollectionInProgress && isGlobal);

    const shouldBeStoppedGeneral =
        isFindDeviceInProgress ||
        isYapFirmwareInProgress ||
        isFirmwareInProgress ||
        isDeviceSyncInProgress ||
        isHolderSyncInProgress ||
        !isCanBeStarted;

    const isHolderDataCollectionShouldBeStopped =
        isNeedCancel &&
        isHolder &&
        (shouldBeStoppedGeneral ||
            (isGlobal && isHolderDataCollectionReceived) ||
            !isHolderConnected ||
            !isHolderRegistered ||
            isHolderInSBLMode);

    const isDeviceDataCollectionShouldBeStopped =
        isNeedCancel &&
        !isHolder &&
        (shouldBeStoppedGeneral ||
            (isGlobal && isDeviceDataCollectionReceived) ||
            !isDeviceRegistered ||
            !isActivated ||
            is_device_in_sbl_mode);

    const isShouldBeStopped = isHolderDataCollectionShouldBeStopped || isDeviceDataCollectionShouldBeStopped;

    return isShouldBeStopped;
};

export const isDataCollectionCanBeStarted = ({
    dataCollectionTypeData,
    iotProduct,
    isDeviceActivated,
    isUsage,
    isDiagnostic,
    isDeviceReady,
    isYapDeviceInfoReceived,
    isDeviceSyncInProgress,
    isHolderSyncInProgress,
    isAutoCleaningInProgress,
    isYapUpdateSettingsInProgress,
    isYapHolderUpdateSettingsInProgress,
    isYapActivationInProgress,
    isYapFirmwareInProgress,
    isFirmwareInProgress,
    isYapHolderInfoReceived,
    isInProgress,
    isCanBeStarted,
    isFindDeviceInProgress,
}) => {
    const {device, holder} = iotProduct || {};
    const {status: deviceStatus, is_device_in_sbl_mode, isP4, isHolderConnected, type} = device || {};
    const {status: holderStatus} = holder || {};
    const isHolderInSBLMode = iotDeviceSettingsService.isHolderInSBLMode(iotProduct);
    const isDeviceRegistered = productService.getIsRegistered(deviceStatus);
    const isHolderRegistered = productService.getIsRegistered(holderStatus);
    const isActivated = isP4 ? isDeviceActivated : true;
    const isDeviceDataCollectionReceived = dataCollectionTypeData.isDeviceDataCollectionReceived;
    const isHolderDataCollectionReceived = dataCollectionTypeData.isHolderDataCollectionReceived;
    const isHolderStartFirst = isHolderSyncFirst(type);
    const isHolderCanStart = (isHolderStartFirst && isUsage) || !isUsage;
    const isDeviceCanStart = (!isHolderStartFirst && isUsage) || !isUsage;
    const isHolderFirst = !isDeviceRegistered || (isHolderStartFirst && !isDiagnostic);
    const isStartHolderSyncFirstly = isHolderFirst ? true : isDeviceDataCollectionReceived;

    const isHolderDataCollectionCanBeStarted =
        !isInProgress &&
        !isHolderDataCollectionReceived &&
        isDeviceReady &&
        isHolderConnected &&
        isHolderRegistered &&
        isYapDeviceInfoReceived &&
        !isDeviceSyncInProgress &&
        !isHolderSyncInProgress &&
        !isAutoCleaningInProgress &&
        !isYapUpdateSettingsInProgress &&
        !isFindDeviceInProgress &&
        !isYapHolderUpdateSettingsInProgress &&
        !isYapActivationInProgress &&
        !isYapFirmwareInProgress &&
        !isFirmwareInProgress &&
        isYapHolderInfoReceived &&
        !isHolderInSBLMode &&
        isCanBeStarted &&
        isStartHolderSyncFirstly &&
        isHolderCanStart;

    const isStartDeviceSyncFirstly = isHolderFirst ? !isHolderDataCollectionCanBeStarted : true;

    const isDeviceDataCollectionCanBeStarted =
        !isInProgress &&
        !isDeviceDataCollectionReceived &&
        isDeviceReady &&
        isYapDeviceInfoReceived &&
        isDeviceRegistered &&
        !isDeviceSyncInProgress &&
        !isHolderSyncInProgress &&
        !isAutoCleaningInProgress &&
        !isYapUpdateSettingsInProgress &&
        !isFindDeviceInProgress &&
        !isYapHolderUpdateSettingsInProgress &&
        !isYapActivationInProgress &&
        !isYapFirmwareInProgress &&
        !isFirmwareInProgress &&
        isActivated &&
        !is_device_in_sbl_mode &&
        isCanBeStarted &&
        isStartDeviceSyncFirstly &&
        isDeviceCanStart;

    const isStartDataCollection = isHolderDataCollectionCanBeStarted || isDeviceDataCollectionCanBeStarted;

    return {isStartDataCollection, isHolder: isHolderDataCollectionCanBeStarted};
};
